import React from 'react';
import { Card, Heading, Box, Text, Flex, Button, Select, TextField, Tooltip, TooltipTrigger, Switch } from '@chatsdet/ui';
import { Lock, Eye, EyeSlash, FloppyDisk, Info } from '@phosphor-icons/react';
import { parseInstanceSize, type InstanceSize } from '@chatsdet/types';
import type { EnvironmentTemplate, EmulationTemplateConfig } from '../../../hooks/useEnvironmentTemplates';
import { INSTANCE_SIZE_OPTIONS } from '../instance-size-options';

/** Available emulation plugins with their UI metadata */
const EMULATION_PLUGINS = [
  {
    id: 'jira',
    name: 'Jira Cloud',
    description: 'Emulates Jira Cloud REST API with configurable projects and issues',
    configFields: [
      { key: 'tenant', label: 'Tenant', placeholder: 'acme', required: true },
    ],
  },
] as const;

/** Shared section title + helper-text block — consistent hierarchy and contrast. */
function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="space-y-1">
      <Heading level={3} size="md">
        {title}
      </Heading>
      <Text size="sm" className="text-muted-foreground">
        {description}
      </Text>
    </div>
  );
}

/** Two-column grid shared by the config table header row and each value row. */
const CONFIG_GRID = 'grid grid-cols-[minmax(140px,1fr)_minmax(0,2fr)] items-center gap-4';

interface ConfigurationTabProps {
  template: EnvironmentTemplate;
  configDefaults: Record<string, string>;
  configuredSensitiveKeys: string[];
  unsetConfigKeys: string[];
  visibleFields: Record<string, boolean>;
  isDirty: boolean;
  isSaving: boolean;
  onUpdateConfig: (key: string, value: string) => void;
  onClearConfig: (key: string) => void;
  onToggleVisibility: (key: string) => void;
  onSave: () => void;
  emulationConfig: EmulationTemplateConfig;
  emulationDirty: boolean;
  isEmulationSaving: boolean;
  onUpdateEmulation: (config: EmulationTemplateConfig) => void;
  onSaveEmulation: () => void;
  instanceSize: InstanceSize;
  onInstanceSizeChange: (next: InstanceSize) => void;
  isInstanceSizeSaving: boolean;
}

export const ConfigurationTab: React.FC<ConfigurationTabProps> = ({
  template,
  configDefaults,
  configuredSensitiveKeys,
  unsetConfigKeys,
  visibleFields,
  isDirty,
  isSaving,
  onUpdateConfig,
  onClearConfig,
  onToggleVisibility,
  onSave,
  emulationConfig,
  emulationDirty,
  isEmulationSaving,
  onUpdateEmulation,
  onSaveEmulation,
  instanceSize,
  onInstanceSizeChange,
  isInstanceSizeSaving,
}) => {
  const sortedConfig = React.useMemo(() => {
    if (!template.configSchema) return [];
    return [...template.configSchema].sort((a, b) => {
      if (a.required !== b.required) return a.required ? -1 : 1;
      return a.key.localeCompare(b.key);
    });
  }, [template.configSchema]);

  const hasConfig = sortedConfig.length > 0;
  const configuredSensitiveAutoConfigKeys = template.configuredSensitiveAutoConfigKeys ?? [];
  const hasAutoConfig =
    (template.autoConfig && Object.keys(template.autoConfig).length > 0) ||
    configuredSensitiveAutoConfigKeys.length > 0;
  const configuredSensitiveKeySet = React.useMemo(
    () => new Set(configuredSensitiveKeys),
    [configuredSensitiveKeys]
  );
  const unsetConfigKeySet = React.useMemo(() => new Set(unsetConfigKeys), [unsetConfigKeys]);

  return (
    <div className="space-y-6">
      {/* Instance Size */}
      <Card padding="md" className="gap-y-4">
        <SectionHeader
          title="Instance size"
          description="Next provision will use the selected size. Already-running instances keep their current size."
        />
        <Box className="max-w-sm">
          <Select
            items={INSTANCE_SIZE_OPTIONS}
            selectedKey={instanceSize}
            onSelectionChange={(key) => {
              const next = parseInstanceSize(String(key));
              if (next && next !== instanceSize) onInstanceSizeChange(next);
            }}
            isDisabled={isInstanceSizeSaving}
            aria-label="Instance size"
          />
        </Box>
      </Card>

      {/* Default Configuration */}
      <Card padding="md" className="gap-y-4">
        <SectionHeader
          title="Default configuration"
          description="Default values for environment variables. Pre-filled when launching instances."
        />

        {hasConfig ? (
          <div className="rounded-lg border border-border overflow-hidden">
            <div
              className={`${CONFIG_GRID} bg-muted px-4 py-2.5 border-b border-border`}
            >
              <Text size="xs" weight="semibold" className="uppercase tracking-wider text-muted-foreground">
                Variable
              </Text>
              <Text size="xs" weight="semibold" className="uppercase tracking-wider text-muted-foreground">
                Default value
              </Text>
            </div>
            <div className="divide-y divide-border">
              {sortedConfig.map((item) => {
                const value = configDefaults[item.key] ?? '';
                const isVisible = visibleFields[item.key];
                const isConfiguredSensitive =
                  item.sensitive &&
                  configuredSensitiveKeySet.has(item.key) &&
                  !unsetConfigKeySet.has(item.key);
                const isPendingUnset = unsetConfigKeySet.has(item.key);

                return (
                  <div key={item.key} className={`${CONFIG_GRID} px-4 py-3`}>
                    <Flex align="center" gap="xs" className="min-w-0">
                      {item.description ? (
                        <TooltipTrigger>
                          <Flex align="center" gap="xs" className="cursor-help min-w-0">
                            <Text size="sm" weight="medium" className="font-mono truncate">
                              {item.key}
                            </Text>
                            <Info size={12} className="text-muted-foreground shrink-0" />
                          </Flex>
                          <Tooltip>{item.description}</Tooltip>
                        </TooltipTrigger>
                      ) : (
                        <Text size="sm" weight="medium" className="font-mono truncate">
                          {item.key}
                        </Text>
                      )}
                      {item.required && <Text size="sm" className="text-destructive shrink-0">*</Text>}
                      {item.sensitive && <Lock size={12} className="text-muted-foreground shrink-0" />}
                    </Flex>
                    <Flex align="center" gap="xs">
                      <TextField
                        value={value}
                        onChange={(v) => onUpdateConfig(item.key, v)}
                        type={item.sensitive && !isVisible ? 'password' : 'text'}
                        placeholder={
                          isPendingUnset
                            ? 'Will be cleared on save'
                            : isConfiguredSensitive
                              ? 'Configured — enter a replacement'
                              : 'Not set'
                        }
                        className="flex-1"
                      />
                      {item.sensitive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          onPress={() => onToggleVisibility(item.key)}
                          aria-label={isVisible ? 'Hide value' : 'Show value'}
                        >
                          {isVisible ? <EyeSlash size={16} /> : <Eye size={16} />}
                        </Button>
                      )}
                      {isConfiguredSensitive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onPress={() => onClearConfig(item.key)}
                          aria-label={`Clear ${item.key}`}
                        >
                          Clear
                        </Button>
                      )}
                    </Flex>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <Text size="sm" className="text-muted-foreground italic">
            No configuration required for this template.
          </Text>
        )}

        {isDirty && (
          <Flex justify="end">
            <Button
              variant="primary"
              size="sm"
              onPress={onSave}
              isDisabled={isSaving}
              iconLeft={<FloppyDisk size={16} />}
            >
              {isSaving ? 'Saving...' : 'Save Defaults'}
            </Button>
          </Flex>
        )}
      </Card>

      {/* Auto-Generated Configuration */}
      {hasAutoConfig && (
        <Card padding="md" className="gap-y-4">
          <SectionHeader
            title="Auto-generated"
            description="These values are automatically configured when the environment starts."
          />
          <div className="rounded-lg border border-border bg-muted/30">
            <pre className="p-3 text-sm font-mono overflow-x-auto">
              {Object.entries(template.autoConfig ?? {}).map(([key, value]) => (
                <div key={key}>
                  <span className="text-blue-500">{key}</span>
                  <span className="text-muted-foreground">=</span>
                  <span className="text-green-500">{value}</span>
                </div>
              ))}
              {configuredSensitiveAutoConfigKeys.map((key) => (
                <div key={key}>
                  <span className="text-blue-500">{key}</span>
                  <span className="text-muted-foreground">=</span>
                  <span className="text-muted-foreground">•••••••• (configured)</span>
                </div>
              ))}
            </pre>
          </div>
        </Card>
      )}

      {/* Emulations */}
      <Card padding="md" className="gap-y-4">
        <Flex align="center" justify="between" gap="md">
          <SectionHeader
            title="Emulations"
            description="Intercept third-party services with emulated responses during test runs."
          />
          <Switch
            isSelected={emulationConfig.enabled}
            onChange={(enabled) => onUpdateEmulation({ ...emulationConfig, enabled })}
            size="sm"
          />
        </Flex>

        {emulationConfig.enabled && (
          <div className="space-y-3">
            {EMULATION_PLUGINS.map((plugin) => {
              const entry = emulationConfig.plugins.find((p) => p.pluginId === plugin.id);
              const isEnabled = entry?.enabled ?? false;

              return (
                <div key={plugin.id} className="border border-border rounded-lg p-4">
                  <Flex align="center" justify="between" gap="md" className="mb-1">
                    <div>
                      <Text size="sm" weight="medium">{plugin.name}</Text>
                      <Text size="sm" className="text-muted-foreground">
                        {plugin.description}
                      </Text>
                    </div>
                    <Switch
                      isSelected={isEnabled}
                      onChange={(selected) => {
                        const plugins = emulationConfig.plugins.filter(
                          (p) => p.pluginId !== plugin.id
                        );
                        plugins.push({
                          pluginId: plugin.id,
                          enabled: selected,
                          config: entry?.config ?? {},
                          fixture: entry?.fixture,
                        });
                        onUpdateEmulation({ ...emulationConfig, plugins });
                      }}
                      size="sm"
                    />
                  </Flex>

                  {isEnabled && plugin.configFields.length > 0 && (
                    <div className="mt-3 space-y-2 pl-1">
                      {plugin.configFields.map((field) => (
                        <Flex key={field.key} align="center" gap="sm">
                          <Text size="sm" className="text-muted-foreground w-24 shrink-0">
                            {field.label}{field.required && <span className="text-destructive">*</span>}
                          </Text>
                          <TextField
                            value={(entry?.config[field.key] as string) ?? ''}
                            onChange={(value) => {
                              const plugins = emulationConfig.plugins.map((p) =>
                                p.pluginId === plugin.id
                                  ? { ...p, config: { ...p.config, [field.key]: value } }
                                  : p
                              );
                              onUpdateEmulation({ ...emulationConfig, plugins });
                            }}
                            placeholder={field.placeholder}
                            className="flex-1"
                          />
                        </Flex>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {emulationDirty && (
          <Flex justify="end">
            <Button
              variant="primary"
              size="sm"
              onPress={onSaveEmulation}
              isDisabled={isEmulationSaving}
              iconLeft={<FloppyDisk size={16} />}
            >
              {isEmulationSaving ? 'Saving...' : 'Save Emulation Config'}
            </Button>
          </Flex>
        )}
      </Card>
    </div>
  );
};
